// TermsPage.jsx
// Public route — no auth required.
// Route: /vocab/terms

import { Mascot } from "@/components/styleguide/Mascot";

export default function TermsPage() {
  return (
    <div className="min-h-[100svh] bg-background">
      {/* ── Header ── */}
      <header className="sticky top-0 z-10 border-b border-border bg-background/90 backdrop-blur-sm">
        <div className="mx-auto flex max-w-2xl items-center justify-between px-5 py-3">
          <a
            href="/vocab"
            className="flex items-center gap-2"
            aria-label="Back to Bloom home"
          >
            <Mascot size={30} animated={false} />
            <span className="font-display text-lg font-black tracking-tight text-foreground">
              Bloom
            </span>
          </a>
          <a
            href="/vocab"
            className="text-xs font-bold text-[#FF7A45] underline underline-offset-2 transition hover:opacity-75"
          >
            ← Back to Bloom
          </a>
        </div>
      </header>

      {/* ── Content ── */}
      <main className="mx-auto max-w-2xl px-5 py-10 sm:py-14">
        {/* Title */}
        <div className="mb-8 border-b border-border pb-6">
          <p className="mb-1 text-xs font-bold uppercase tracking-widest text-[#FF7A45]">
            Legal
          </p>
          <h1 className="font-display text-3xl font-black text-foreground sm:text-4xl">
            Terms &amp; Conditions
          </h1>
          <p className="mt-2 text-sm font-semibold text-muted-foreground">
            Last updated: October 2026
          </p>
        </div>

        <div className="space-y-8 text-sm font-semibold leading-relaxed text-muted-foreground">

          {/* Intro */}
          <section>
            <p>
              Welcome to Bloom! These Terms &amp; Conditions explain how you can use
              Bloom, the responsibilities of users and the terms that apply to
              subscriptions. By creating an account or using Bloom, you agree to these
              terms. Please read them carefully.
            </p>
            <p className="mt-3">
              If you have questions, contact us at <EmailLink />.
            </p>
          </section>

          {/* 1. About Bloom */}
          <section>
            <H2>1. About Bloom</H2>
            <p>
              Bloom is a vocabulary-learning application designed to help children in
              Years 3–6 develop their English vocabulary and practise skills relevant to
              11+ and grammar school entrance examinations. Bloom provides learning
              activities, vocabulary content, quizzes and progress-tracking features.
            </p>
            <p className="mt-3">
              Bloom is developed and operated by{" "}
              <strong className="text-foreground">Rohan Solutions Ltd</strong>, trading as{" "}
              <strong className="text-foreground">We Built Here</strong>, a company
              registered in England and Wales. In these terms, "Bloom", "we", "us" and
              "our" refer to Rohan Solutions Ltd.
            </p>
          </section>

          {/* 2. Who can use */}
          <section>
            <H2>2. Who can use Bloom</H2>
            <ul className="space-y-3">
              <Li>
                Bloom accounts are intended for{" "}
                <strong className="text-foreground">adults aged 18 or over</strong>,
                including parents and guardians who create and manage profiles for children
                in their care.
              </Li>
              <Li>
                Children use Bloom through a parent or guardian's account. By creating an
                account, you confirm that you are at least 18 years old and that any child
                profile you create is for a child you are entitled to care for or supervise.
              </Li>
              <Li>
                You are responsible for supervising your child's use of Bloom in an
                age-appropriate manner.
              </Li>
            </ul>
          </section>

          {/* 3. Account */}
          <section>
            <H2>3. Your account</H2>
            <ul className="space-y-3">
              <Li>
                You sign in to Bloom using your Google account. You are responsible for
                keeping your Google account secure and for taking reasonable steps to
                prevent unauthorised access to your Bloom account. Please contact us
                promptly if you believe someone has accessed your account without
                permission.
              </Li>
              <Li>
                You may create up to two child profiles per account, subject to the
                features available in your plan. Each profile may use a nickname — you do
                not need to provide a child's real name.
              </Li>
              <Li>
                You can delete child profiles or request account deletion through your
                account settings. Deletion is subject to the applicable data-retention
                requirements described in our{" "}
                <InternalLink href="/vocab/privacy">Privacy Policy</InternalLink>.
              </Li>
              <Li>
                You must provide accurate information when creating and managing your
                account and must not misuse another person's account.
              </Li>
            </ul>
          </section>

          {/* 4. Free trial */}
          <section>
            <H2>4. Free trial</H2>
            <p>
              New accounts currently include a{" "}
              <strong className="text-foreground">seven-day free trial</strong> with access
              to the features specified at sign-up. No payment card details are required to
              start the trial.
            </p>
            <p className="mt-3">
              At the end of the trial, you may choose a paid subscription to continue
              accessing paid features. If you do not subscribe, access to those features
              will pause — we may retain your account information in accordance with our
              Privacy Policy.
            </p>
            <p className="mt-3">
              The trial does not automatically convert into a paid subscription unless you
              separately agree to a paid plan through the applicable purchase process. We
              may change the availability or duration of future promotional trials, subject
              to any commitments already made to customers.
            </p>
          </section>

          {/* 5. Subscriptions */}
          <section>
            <H2>5. Subscriptions and pricing</H2>
            <ul className="space-y-3">
              <Li>
                After the free trial, continued access to paid features requires a
                subscription. Current prices and available plans are displayed before you
                purchase, including the applicable price, billing frequency and relevant
                subscription terms.
              </Li>
              <Li>
                Subscriptions may be offered monthly or annually, depending on the plans
                available. Where a subscription renews automatically, the renewal
                arrangements, renewal price and billing frequency will be disclosed before
                you agree to the subscription.
              </Li>
              <Li>
                Payments are processed by Stripe or another payment provider identified at
                checkout. You are responsible for ensuring that your payment arrangements
                are valid and that subscription payments can be collected as agreed.
              </Li>
            </ul>
          </section>

          {/* 6. Promotions */}
          <section>
            <H2>6. Launch promotions and discounts</H2>
            <p>
              Bloom may offer promotional prices, launch offers or discounts from time to
              time. Each offer will be subject to the eligibility criteria, price, duration
              and other conditions displayed when the offer is advertised or purchased.
            </p>
            <p className="mt-3">
              Where an offer applies to an annual subscription, the promotional price and
              any subsequent renewal price will be shown before you commit. Promotional
              offers cannot be combined unless the offer expressly allows this. We will not
              retrospectively change the agreed price of an existing subscription except
              where permitted by the subscription terms and applicable law.
            </p>
          </section>

          {/* 7. Cancelling */}
          <section>
            <H2>7. Cancelling a subscription</H2>
            <ul className="space-y-3">
              <Li>
                You can cancel your subscription using the cancellation option in your
                account or by contacting <EmailLink />. We will explain when cancellation
                takes effect and whether any further payment is due.
              </Li>
              <Li>
                Unless applicable law or the terms of your purchase provide otherwise,
                cancellation takes effect at the end of the current billing period. You
                will normally retain access to paid features until that period ends.
              </Li>
              <Li>
                Cancelling a subscription does not automatically delete your account or
                child profiles — you can request deletion separately.
              </Li>
              <Li>
                If you cancel during a free trial before agreeing to a paid subscription,
                you will not be charged. Nothing in these terms removes any cancellation
                rights you may have under applicable consumer law.
              </Li>
            </ul>
          </section>

          {/* 8. Refunds */}
          <section>
            <H2>8. Refunds and consumer rights</H2>
            <p>
              If you are not satisfied with Bloom, you can contact <EmailLink /> to request
              a refund. Our voluntary refund policy allows you to request a refund within{" "}
              <strong className="text-foreground">14 days of your first payment</strong>,
              subject to the details of your purchase.
            </p>
            <p className="mt-3">
              After this period, we do not normally offer refunds for the unused portion of
              a billing period, except where required by law or where we decide otherwise
              in the circumstances.
            </p>
            <p className="mt-3">
              Your statutory consumer rights are not affected by this voluntary refund
              policy. Depending on the type of contract and the circumstances, you may have
              additional cancellation or refund rights under applicable consumer law,
              including rules relating to digital content and digital services.
            </p>
            <p className="mt-3">
              To request a refund, email <EmailLink /> with your account email address and
              relevant purchase details. You can also read our{" "}
              <InternalLink href="/vocab/refund-policy">Refund Policy</InternalLink> for
              full details.
            </p>
          </section>

          {/* 9. IP */}
          <section>
            <H2>9. Bloom's content and intellectual property</H2>
            <p>
              Bloom includes vocabulary content, educational materials, illustrations,
              characters, graphics, designs, logos, software and other creative material.
              To the extent that the relevant rights are owned by Rohan Solutions Ltd,
              those rights remain with Rohan Solutions Ltd.
            </p>
            <p className="mt-3">
              You may use Bloom's content for your personal, non-commercial learning within
              the app. Unless permitted by law or authorised by us in writing, you must not:
            </p>
            <ul className="mt-3 space-y-3">
              <Li>
                Copy, reproduce or redistribute Bloom's content for commercial purposes.
              </Li>
              <Li>
                Publish or sell Bloom's illustrations, vocabulary cards or educational
                materials as your own.
              </Li>
              <Li>Remove copyright or attribution notices.</Li>
              <Li>
                Reproduce or exploit Bloom's software, branding or original materials
                outside the permitted use of the app.
              </Li>
            </ul>
            <p className="mt-3">
              The Bloom name, logo and mascot may be protected by intellectual property
              rights. These restrictions do not override rights or exceptions provided by
              applicable law.
            </p>
          </section>

          {/* 10. Acceptable use */}
          <section>
            <H2>10. Acceptable use</H2>
            <p className="mb-3">You agree not to:</p>
            <ul className="space-y-3">
              <Li>
                Share your account access with people outside your household in a way that
                circumvents the intended account limits.
              </Li>
              <Li>Use Bloom for commercial purposes without our permission.</Li>
              <Li>
                Attempt to reverse-engineer, copy or reproduce the app or its content
                beyond what is permitted by law.
              </Li>
              <Li>
                Attempt to gain unauthorised access to accounts, systems or information.
              </Li>
              <Li>
                Introduce malicious software or interfere with Bloom's operation.
              </Li>
              <Li>
                Use Bloom in a way that could damage, disrupt or overload the service.
              </Li>
              <Li>Use Bloom unlawfully or infringe another person's rights.</Li>
            </ul>
            <p className="mt-3">
              We may restrict or suspend access where reasonably necessary to protect Bloom,
              its users or its systems, or to address a serious breach of these terms. Where
              appropriate, we will explain the reason and provide an opportunity to resolve
              the issue.
            </p>
          </section>

          {/* 11. Educational content */}
          <section>
            <H2>11. Educational content and exam outcomes</H2>
            <p>
              Bloom is an educational support tool intended to help children practise
              vocabulary and develop their English skills. We aim to provide accurate,
              helpful and age-appropriate learning content. However, despite our best
              efforts, occasional errors or inaccuracies may occur, including in word
              meanings, synonyms, antonyms, spellings, example sentences, illustrations
              or quiz questions and answers.
            </p>
            <p className="mt-3">
              Parents and guardians should use their own judgement when supporting a
              child's learning. Bloom does not guarantee any particular examination score,
              admission result or place at a grammar school or other educational
              institution, as exam outcomes depend on many factors outside our control.
            </p>
            <p className="mt-3">
              If you identify a content error, please contact <EmailLink /> so that we
              can review it and make corrections where appropriate.
            </p>
          </section>

          {/* 12. Availability */}
          <section>
            <H2>12. Availability and changes to Bloom</H2>
            <p>
              We work to keep Bloom available and reliable, but we cannot guarantee
              uninterrupted access. We may need to carry out maintenance, fix technical
              issues or improve features from time to time.
            </p>
            <p className="mt-3">
              We may add, modify or remove features over time. Where a change materially
              affects a paid service or your contractual rights, we will take appropriate
              steps in accordance with applicable law and the terms of your subscription.
            </p>
          </section>

          {/* 13. Our responsibility */}
          <section>
            <H2>13. Our responsibility</H2>
            <p>
              We take reasonable care in providing Bloom and maintaining its features. To
              the extent permitted by law, we are not responsible for interruptions caused
              by circumstances beyond our reasonable control, or for loss arising from use
              of Bloom in a way that is contrary to these terms.
            </p>
            <p className="mt-3">
              Nothing in these terms excludes or limits liability where doing so would be
              unlawful. In particular, nothing here affects consumer rights or any
              liability that cannot legally be excluded or limited. We do not guarantee
              exam results or admission outcomes, as explained in Section 11.
            </p>
          </section>

          {/* 14. Suspension */}
          <section>
            <H2>14. Account suspension and termination</H2>
            <ul className="space-y-3">
              <Li>
                You may stop using Bloom at any time and request account deletion through
                your settings or by contacting us.
              </Li>
              <Li>
                We may suspend or terminate an account where reasonably necessary because
                of a serious breach of these terms, unlawful activity, security concerns or
                misuse of the service. Where appropriate, we will notify you of the reason
                and allow an opportunity to address the issue before termination.
              </Li>
              <Li>
                If we discontinue Bloom or terminate a paid service, we will handle any
                outstanding subscription obligations and applicable refunds in accordance
                with the contract and applicable law.
              </Li>
            </ul>
          </section>

          {/* 15. Changes to terms */}
          <section>
            <H2>15. Changes to these terms</H2>
            <p>
              We may update these terms when Bloom's features, services or legal
              requirements change. For significant changes, we will take reasonable steps
              to notify you — for example by email or through a notice in the app.
            </p>
            <p className="mt-3">
              Where required by law or where a change requires your agreement, we will
              obtain that agreement. If a material change affects an existing paid
              subscription, we will explain its effect and any applicable options available
              to you. The date at the top of this page indicates when the terms were last
              updated.
            </p>
          </section>

          {/* 16. Privacy */}
          <section>
            <H2>16. Privacy</H2>
            <p>
              Our Privacy Policy explains how we collect, use, store and protect personal
              information. Please review it alongside these terms:{" "}
              <InternalLink href="/vocab/privacy">Privacy Policy</InternalLink>.
            </p>
          </section>

          {/* 17. Governing law */}
          <section>
            <H2>17. Governing law and disputes</H2>
            <p>
              These terms are governed by the laws of England and Wales, subject to any
              mandatory consumer protection laws that apply to you.
            </p>
            <p className="mt-3">
              If you have a concern or dispute, please contact <EmailLink /> first so that
              we can try to resolve it fairly and amicably. Nothing in this section limits
              any rights you may have to bring a claim before a court with appropriate
              jurisdiction.
            </p>
          </section>

          {/* 18. Contact */}
          <section>
            <H2>18. Contact us</H2>
            <p>
              For questions about Bloom, subscriptions, these terms or technical support,
              please get in touch — we're a small team and we're always happy to help.
            </p>
            <p className="mt-3">
              <strong className="text-foreground">
                Rohan Solutions Ltd, trading as We Built Here
              </strong>
              <br />
              Email: <EmailLink />
            </p>
          </section>

          {/* Legal footer */}
          <div className="border-t border-border pt-6 text-xs text-muted-foreground">
            Bloom is a product of{" "}
            <strong className="text-foreground">Rohan Solutions Ltd</strong>, registered
            in England and Wales. These terms are governed by the laws of England and
            Wales.{" "}
            <InternalLink href="/vocab/privacy">Privacy Policy</InternalLink>
            {" · "}
            <InternalLink href="/vocab/refund-policy">Refund Policy</InternalLink>
          </div>
        </div>
      </main>
    </div>
  );
}

// ─── Local helpers ────────────────────────────────────────────────────────────
function H2({ children }) {
  return (
    <h2 className="mb-3 font-display text-base font-black text-foreground sm:text-lg">
      {children}
    </h2>
  );
}

function Li({ children }) {
  return (
    <li className="flex gap-2.5">
      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#FF7A45]" />
      <span>{children}</span>
    </li>
  );
}

function EmailLink() {
  return (
    <a
      href="mailto:team@webuilthere.com"
      className="text-[#FF7A45] underline underline-offset-2 transition hover:opacity-75"
    >
      team@webuilthere.com
    </a>
  );
}

function InternalLink({ href, children }) {
  return (
    <a
      href={href}
      className="text-[#FF7A45] underline underline-offset-2 transition hover:opacity-75"
    >
      {children}
    </a>
  );
}